/**
 * Council Trail v0 — lens state serialization.
 *
 * Round-trips between LensSerializedState and:
 *   - URLSearchParams (sharable links + browser-history-friendly state)
 *   - localStorage   (per-session persistence across reloads)
 *
 * Both surfaces are tolerant: malformed/missing fields produce Partial<>
 * (callers merge with defaults), and storage exceptions (quota, disabled
 * storage) degrade silently.
 */
import type { LensMode } from '../types/council';

export interface LensSerializedState {
  mode: LensMode;
  activePersonaIds: Set<string>;
  activeTrailId: string | null;
  cursorTurnId: string | null;
}

const LS_KEY = (sessionId: string): string => `council.lens.${sessionId}`;

export function lensStateToUrlParams(state: LensSerializedState): URLSearchParams {
  const p = new URLSearchParams();
  p.set('lens', state.mode);
  if (state.activePersonaIds.size > 0) {
    p.set('peers', Array.from(state.activePersonaIds).join(','));
  }
  if (state.activeTrailId) p.set('trail', state.activeTrailId);
  if (state.cursorTurnId) p.set('cursor', state.cursorTurnId);
  return p;
}

export function lensStateFromUrlParams(
  params: URLSearchParams,
): Partial<LensSerializedState> {
  const mode = (params.get('lens') ?? undefined) as LensMode | undefined;
  const peersCsv = params.get('peers');
  const peers = peersCsv
    ? new Set(peersCsv.split(',').filter(Boolean))
    : new Set<string>();
  return {
    mode,
    activePersonaIds: peers,
    activeTrailId: params.get('trail'),
    cursorTurnId: params.get('cursor'),
  };
}

export function lensStateToLocalStorage(
  sessionId: string,
  state: LensSerializedState,
): void {
  try {
    localStorage.setItem(LS_KEY(sessionId), JSON.stringify({
      mode: state.mode,
      activePersonaIds: Array.from(state.activePersonaIds),
      activeTrailId: state.activeTrailId,
      cursorTurnId: state.cursorTurnId,
    }));
  } catch {
    // quota exceeded / storage disabled — degrade silently
  }
}

export function lensStateFromLocalStorage(
  sessionId: string,
): Partial<LensSerializedState> | null {
  try {
    const raw = localStorage.getItem(LS_KEY(sessionId));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return {
      mode: obj.mode,
      activePersonaIds: new Set(obj.activePersonaIds ?? []),
      activeTrailId: obj.activeTrailId ?? null,
      cursorTurnId: obj.cursorTurnId ?? null,
    };
  } catch {
    return null;
  }
}
